/**
 * Single source of truth for all the app's colors.
 * Two palettes: light and dark. The active mode is managed by ThemeContext.
 */

// ─── Light palette ───────────────────────────────────────────────────────────
export const lightColors = {
  // Backgrounds
  // bgDefault is the page/chat body; bgPaper is for surfaces that need to
  // visually stand out (right panels, input bar, cards). Keeping a clear
  // gap between the two is what gives light mode its sense of layering.
  bgDefault:     '#EEF2F7',
  bgPaper:       '#FFFFFF',
  bgSubtle:      '#F8FAFC',
  bgMuted:       '#F1F5F9',

  // Texte
  textPrimary:   '#0F172A',
  textSecondary: '#64748B',
  textMuted:     '#94A3B8',
  textInverse:   '#FFFFFF',

  // Bordures
  border:        '#E5E7EB',
  borderStrong:  '#D1D5DB',
  borderSubtle:  '#F1F5F9',

  // Accents — primaire (indigo)
  primary:       '#4F46E5',
  primaryLight:  '#818CF8',
  primaryDark:   '#4338CA',
  primaryHover:  '#4338CA',
  primaryBg:     'rgba(79, 70, 229, 0.08)',

  // Accents — secondaire (violet)
  secondary:     '#A855F7',
  secondaryLight: '#C084FC',
  secondaryDark: '#9333EA',

  // Status
  success:       '#059669',
  successLight:  '#34D399',
  successBg:     'rgba(5, 150, 105, 0.08)',
  warning:       '#D97706',
  warningLight:  '#F59E0B',
  warningBg:     'rgba(217, 119, 6, 0.08)',
  warningBgSolid: '#FEF3C7',
  error:         '#DC2626',
  errorLight:    '#F87171',
  errorBg:       'rgba(220, 38, 38, 0.08)',
  info:          '#0EA5E9',
  infoLight:     '#38BDF8',
  infoBg:        'rgba(14, 165, 233, 0.08)',

  // SACEM roles
  roleAuteur:        '#A855F7',
  roleCompositeur:   '#0EA5E9',
  roleArrangeur:     '#F59E0B',
  roleEditeur:       '#38BDF8',
  roleEACA:          '#34D399',

  // Gradients
  gradientPrimary:      'linear-gradient(135deg, #4F46E5, #A855F7)',
  gradientPrimaryHover: 'linear-gradient(135deg, #4338CA, #9333EA)',
  gradientHero:         'linear-gradient(135deg, #38BDF8 0%, #3B82F6 40%, #4F46E5 70%, #A855F7 100%)',
  gradientBrand:        'linear-gradient(135deg, #38BDF8, #818CF8, #A855F7)',
  gradientSuccess:      'linear-gradient(135deg, #059669, #10B981)',

  // Overlays & shadows
  overlayLight:  'rgba(0, 0, 0, 0.04)',
  overlayMedium: 'rgba(0, 0, 0, 0.08)',
  shadow:        '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
  shadowMd:      '0 4px 12px rgba(0, 0, 0, 0.08)',
  shadowLg:      '0 10px 30px rgba(0, 0, 0, 0.12)',
};

// ─── Dark palette ────────────────────────────────────────────────────────────
export const darkColors = {
  // Backgrounds
  bgDefault:     '#0F172A',
  bgPaper:       '#1E293B',
  bgSubtle:      '#273449',
  bgMuted:       '#334155',

  // Texte
  textPrimary:   '#F1F5F9',
  textSecondary: '#94A3B8',
  textMuted:     '#64748B',
  textInverse:   '#0F172A',

  // Bordures
  border:        'rgba(255, 255, 255, 0.08)',
  borderStrong:  'rgba(255, 255, 255, 0.15)',
  borderSubtle:  'rgba(255, 255, 255, 0.04)',

  // Accents — primaire (indigo)
  primary:       '#818CF8',
  primaryLight:  '#A5B4FC',
  primaryDark:   '#6366F1',
  primaryHover:  '#6366F1',
  primaryBg:     'rgba(129, 140, 248, 0.12)',

  // Accents — secondaire (violet)
  secondary:     '#C084FC',
  secondaryLight: '#D8B4FE',
  secondaryDark: '#A855F7',

  // Status
  success:       '#10B981',
  successLight:  '#4ADE80',
  successBg:     'rgba(16, 185, 129, 0.12)',
  warning:       '#F59E0B',
  warningLight:  '#FBBF24',
  warningBg:     'rgba(245, 158, 11, 0.12)',
  warningBgSolid: '#422006',
  error:         '#EF4444',
  errorLight:    '#FCA5A5',
  errorBg:       'rgba(239, 68, 68, 0.12)',
  info:          '#38BDF8',
  infoLight:     '#7DD3FC',
  infoBg:        'rgba(56, 189, 248, 0.12)',

  // SACEM roles
  roleAuteur:        '#C084FC',
  roleCompositeur:   '#38BDF8',
  roleArrangeur:     '#FBBF24',
  roleEditeur:       '#7DD3FC',
  roleEACA:          '#4ADE80',

  // Gradients
  gradientPrimary:      'linear-gradient(135deg, #6366F1, #A855F7)',
  gradientPrimaryHover: 'linear-gradient(135deg, #4F46E5, #9333EA)',
  gradientHero:         'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
  gradientBrand:        'linear-gradient(135deg, #38BDF8, #818CF8, #A855F7)',
  gradientSuccess:      'linear-gradient(135deg, #059669, #10B981)',

  // Overlays & shadows
  overlayLight:  'rgba(255, 255, 255, 0.02)',
  overlayMedium: 'rgba(255, 255, 255, 0.05)',
  shadow:        '0 1px 3px rgba(0, 0, 0, 0.3)',
  shadowMd:      '0 4px 12px rgba(0, 0, 0, 0.4)',
  shadowLg:      '0 10px 30px rgba(0, 0, 0, 0.5)',
};

// Default export — uses a cached palette updated by ThemeContext via setColorMode().
// Components that don't use the useColors() hook still get reactive updates
// because ThemeContext re-renders the tree when mode changes.
let _cachedMode = (typeof window !== 'undefined' && localStorage.getItem('theme-mode')) || 'light';
let _cachedPalette = _cachedMode === 'dark' ? darkColors : lightColors;

/**
 * Update the cached palette. Called by ThemeContext when the user toggles
 * the theme. Avoids reading localStorage on every property access.
 */
export function setColorMode(mode) {
  _cachedMode = mode === 'dark' ? 'dark' : 'light';
  _cachedPalette = _cachedMode === 'dark' ? darkColors : lightColors;
}

export const colors = new Proxy({}, {
  get(_, prop) {
    return _cachedPalette[prop];
  }
});

export default colors;
